import moment from 'moment';
import { fill } from 'lodash';
import { Price } from '@model/common';
import { Basket, RoomBasket } from '@model/iceberg';
import { WebhooksApi } from '@model/iceberg/service';
import { TourContent, TourCheapest } from '@model/tours';
import { RetargetingPayload, RetargetingRoomsPayload, TourRetargetingPayload } from '@model/retargeting';
import {
  DEFAULT_MAX_ROOMS,
  DAY_MONTH_YEAR_FORMAT_LONG,
  LONG_MONTH_YEAR_FORMAT,
  YEAR_MONTH_FORMAT,
  ISO_8601_DATE_FORMAT,
  Amount
} from '@model/common';
import Cookies, { Cookie } from '@model/common/cookie/cookie';
import { MarkdownSanitizer } from '@util/markdown/markdown-sanitizer';
import { getQueryAsObject } from '@util/common';
import { getTourProductUrl } from '@util/tours';

export const performRetargeting = (basket: Basket, shareLink: string) => {
  const cookies = new Cookies();
  const ehid: string | undefined = cookies.get(Cookie.EHID);
  const sfsk: string | undefined = cookies.get(Cookie.SFSK);

  if (sfsk && ehid) {
    const retargetingPayload = formatRetargetingPayload({ sfsk, ehid, basket, url: shareLink });
    new WebhooksApi().retargeting(retargetingPayload);
  }
};

interface FormatRetargetingPayloadParams {
  sfsk: string;
  ehid: string;
  basket: Basket;
  url: string;
}

export const formatRetargetingPayload = ({
  sfsk,
  ehid,
  basket,
  url
}: FormatRetargetingPayloadParams): RetargetingPayload => {
  const { hotel, flight, pricing } = basket;
  const roomOffers: Array<string> =
    hotel?.rooms.reduce(
      (curr: Array<string>, acc: RoomBasket) => (acc.offersApplied.length ? [...curr, ...acc.offersApplied] : curr),
      []
    ) || [];
  const durationAndCheckIn = `${hotel?.nights} nights${
    hotel?.checkInDate ? ` from ${moment(hotel?.checkInDate).format(DAY_MONTH_YEAR_FORMAT_LONG)}` : ''
  }`;
  const outbound = flight?.outbound[0];
  const departure = moment(outbound?.departure.time).format(DAY_MONTH_YEAR_FORMAT_LONG);
  const flights = `Return flights from ${outbound?.departure.name} departing on ${departure}`;

  const roomsData = fill(new Array(DEFAULT_MAX_ROOMS), {}).reduce((_, acc, idx) => {
    const room: any = hotel?.rooms[idx] || {};
    const keyIndex = idx + 1;
    const { roomType, mealPlan, occupancy } = room;
    acc[`room${keyIndex}Name`] = roomType || '';
    acc[`room${keyIndex}BoardBasis`] = mealPlan || '';
    acc[`room${keyIndex}Guests`] = occupancy
      ? `${occupancy.adults} Adults${occupancy.children ? `${occupancy.children} Children` : ''}`
      : '';
    return acc;
  }, {}) as RetargetingRoomsPayload;
  const retargetData = {
    contactId: sfsk,
    link: url,
    propertyName: hotel?.place?.hotel?.display || '',
    propertyReferenceId: `${hotel?.connectId || ''}`,
    totalPrice: new Amount(pricing.total.amount).formatNoSign(),
    pricePp: new Amount(pricing.perPerson.amount).formatNoSign(),
    departureDate: moment(flight?.outbound[0].departure.time).format(ISO_8601_DATE_FORMAT),
    durationAndCheckIn,
    ...roomsData,
    flights,
    specialOffers: roomOffers.join('|'),
    eHid: ehid
  };
  return retargetData;
};

interface PerformTourRetargetingProps {
  basket?: Basket;
  path: string;
  tour: TourContent;
  cheapest?: TourCheapest;
  price?: Price;
  adults?: string;
  departure?: string;
}
export const performTourRetargeting = ({
  basket,
  path,
  tour,
  cheapest,
  price,
  adults,
  departure
}: PerformTourRetargetingProps) => {
  const cookies = new Cookies();
  const ehid: string | undefined = cookies.get(Cookie.EHID);
  const sfsk: string | undefined = cookies.get(Cookie.SFSK);

  if (sfsk && ehid) {
    const { origin, search } = window.location;
    const query = getQueryAsObject(search);
    const link = getTourProductUrl({ productPath: path, ...query });
    const shareLink = `${origin}${link.as}`;

    const retargetingPayload = formatTourRetargetingPayload({
      sfsk,
      ehid,
      basket,
      tour,
      url: shareLink,
      path,
      cheapest,
      price,
      adults,
      departureDate: departure
    });

    new WebhooksApi().tourRetargeting(retargetingPayload);
  }
};

interface FormatTourRetargetingPayloadParams {
  sfsk: string;
  ehid: string;
  url: string;
  path: string;
  basket?: Basket;
  tour: TourContent;
  cheapest?: TourCheapest;
  price?: Price;
  adults?: string;
  departureDate?: string;
}

export const formatTourRetargetingPayload = ({
  sfsk,
  ehid,
  url,
  path,
  basket,
  tour,
  cheapest,
  price,
  adults,
  departureDate
}: FormatTourRetargetingPayloadParams): TourRetargetingPayload => {
  const { flight } = basket || {};
  const viewedPrice = price?.amount || cheapest?.price?.amount || 0;
  const totalAmount = basket ? basket.pricing.total.amount : Number(adults || 1) * viewedPrice;
  const ppAmount = basket ? basket.pricing.perPerson.amount : viewedPrice;
  const outbound = flight?.outbound[0];
  const chosenDepartureDate = outbound?.departure.time || departureDate;
  const formattedDeparture = chosenDepartureDate ? moment(chosenDepartureDate).format(DAY_MONTH_YEAR_FORMAT_LONG) : '';
  const cheapestDeparture = moment({
    year: Number(cheapest?.year)
  }).month(cheapest?.month || '');

  const departure = formattedDeparture ? `on ${formattedDeparture}` : cheapestDeparture.format(LONG_MONTH_YEAR_FORMAT);

  const retargetData = {
    contactId: sfsk,
    link: url,
    totalPrice: new Amount(totalAmount).formatNoSign(),
    pricePp: new Amount(ppAmount).formatNoSign(),
    tourName: tour.name,
    tourDurationAndCheckIn: `${tour.subTitle} departing ${departure}`,
    tourRoomAndBoard: `${tour.mealsCount} Meals Included`,
    tourImageLink: tour.imageUrl,
    flightCarrierMessage: `Includes return flights${
      flight ? ` with ${outbound?.airline} departing from ${outbound?.departure.name}` : ''
    }`,
    tourIncludes: `Includes hand luggage, hold luggage and overseas transfers`,
    tourExcursions: tour.excursionsCount,
    tourDescription: MarkdownSanitizer.sanitize(tour.description),
    tourPath: path,
    eHid: ehid,
    departureDate: chosenDepartureDate
      ? moment(chosenDepartureDate).format(ISO_8601_DATE_FORMAT)
      : cheapestDeparture.format(YEAR_MONTH_FORMAT)
  };
  return retargetData;
};
